import React, { useEffect, useRef, useState } from 'react';
import ReactImageFallback from 'react-image-fallback';
import { LomavisShortenedNumber } from '../../utils/numbers/LomavisShortenedNumber';
import './index.css';
import { getDateTimeFormated } from '@/components/utils/dates/utils';
export type MediaFile = {
  id: string;
  url: string;
  preview_image: string;
  file_type: 'image' | 'video' | 'doc' | 'other';
};

export type Analytics = {
  comments?: number;
  engagement?: number;
  impressions?: number;
  reach?: number;
  reactions?: number;
  shares?: number;
  interactions?: number;
};

export type PostType = 'post' | 'image' | 'multiImage' | 'video';

export type PostData = {
  title: string;
  text: string;
  media: MediaFile[];
  postType: PostType;
  publicationDate: string; // ISO string
  platforms: string[];
  analytics: Analytics;
  profile: {
    name: string;
    imageUrl: string;
  };
};

interface LomavisPostCard2Props {
  /** The text to display inside the H1 heading */

  data: PostData;
}

interface LomavisImageProps extends React.ImgHTMLAttributes<HTMLImageElement> {
  fallback?: string;
}

export const LomavisImage: React.FC<LomavisImageProps> = ({
  src,
  fallback,
  alt = '',
  className = '',
  ...props
}) => {
  const [currentSrc, setCurrentSrc] = useState<string | undefined>(src);
  const [triedFallback, setTriedFallback] = useState(false);
  const [failed, setFailed] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const imgRef = useRef<HTMLImageElement>(null);

  // Reset state when src or fallback changes
  useEffect(() => {
    setCurrentSrc(src);
    setTriedFallback(false);
    setFailed(false);
    setIsLoading(true);
  }, [src, fallback]);

  const handleImgError = () => {
    // console.log('Image error occurred', {
    //   currentSrc,
    //   triedFallback,
    //   fallback
    // });

    // If we haven't tried fallback yet and we have a fallback URL -> try it
    if (!triedFallback && fallback) {
      setTriedFallback(true);
      setCurrentSrc(fallback);
      setIsLoading(true);
    } else {
      // Fallback either already tried or doesn't exist -> show placeholder
      setFailed(true);
      setIsLoading(false);
    }
  };

  const handleImgLoad = (e: React.SyntheticEvent<HTMLImageElement>) => {
    const img = e.currentTarget;

    // Check if the image actually loaded properly by checking dimensions
    // Expired/invalid images often load as 0x0 or very small dimensions
    if (
      img.naturalWidth === 0 ||
      img.naturalHeight === 0 ||
      (img.naturalWidth < 10 && img.naturalHeight < 10)
    ) {
      // console.log('Image loaded but appears invalid', {
      //   naturalWidth: img.naturalWidth,
      //   naturalHeight: img.naturalHeight,
      //   src: img.src
      // });
      handleImgError();
      return;
    }

    setIsLoading(false);
    setFailed(false);
  };

  // Alternative validation using Image constructor
  useEffect(() => {
    if (!currentSrc || failed) return;

    const img = new Image();
    let hasLoaded = false;

    const handleLoadTest = () => {
      hasLoaded = true;
      // Check if it's a valid image by dimensions
      if (
        img.naturalWidth === 0 ||
        img.naturalHeight === 0 ||
        (img.naturalWidth < 10 && img.naturalHeight < 10)
      ) {
        // console.log('Image test failed - invalid dimensions', {
        //   naturalWidth: img.naturalWidth,
        //   naturalHeight: img.naturalHeight,
        //   src: currentSrc
        // });
        handleImgError();
      }
    };

    const handleErrorTest = () => {
      if (!hasLoaded) {
        // console.log('Image test failed - load error', { src: currentSrc });
        handleImgError();
      }
    };

    img.onload = handleLoadTest;
    img.onerror = handleErrorTest;
    img.src = currentSrc;

    // Cleanup timeout to avoid memory leaks
    const timeoutId = setTimeout(() => {
      if (!hasLoaded && !failed) {
        // console.log('Image test timeout', { src: currentSrc });
        handleImgError();
      }
    }, 8000); // 8 second timeout for the test

    return () => {
      clearTimeout(timeoutId);
      img.onload = null;
      img.onerror = null;
    };
  }, [currentSrc, triedFallback]); // Re-run when we try fallback

  // Timeout fallback: if image doesn't load within reasonable time, show fallback
  useEffect(() => {
    if (!currentSrc || failed) return;

    const timeoutId = setTimeout(() => {
      // If still loading after timeout, treat as failed
      if (isLoading && !failed) {
        // console.log('Image loading timeout', { url: currentSrc });
        handleImgError();
      }
    }, 12000); // 12 second timeout

    return () => clearTimeout(timeoutId);
  }, [currentSrc, isLoading, failed]);

  // If no src is provided initially, show placeholder immediately
  useEffect(() => {
    if (!src) {
      setFailed(true);
      setIsLoading(false);
    }
  }, [src]);

  return (
    <div
      className={`position-relative w-100 h-400px overflow-hidden rounded-2 ${className}`}
    >
      {!failed && currentSrc ? (
        <img
          ref={imgRef}
          src={currentSrc}
          alt={alt}
          onError={handleImgError}
          onLoad={handleImgLoad}
          style={{ objectFit: 'cover' }}
          className="w-100 h-100 rounded-2"
          {...props}
        />
      ) : (
        <div className="d-flex w-100 h-100 align-items-center justify-content-center bg-secondary rounded-2">
          <i className="fa-regular fa-image fs-1 text-muted" />
        </div>
      )}

      {/* Loading state (optional) */}
      {isLoading && currentSrc && !failed && (
        <div className="position-absolute top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center bg-light bg-opacity-75">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      )}

      {/* Overlay (keeps your existing overlay behaviour) */}
      <div className="overlay d-flex align-items-center justify-content-center">
        <span className="w-40px h-40px bg-white rounded-circle d-flex justify-content-center align-items-center">
          <i className="fa-regular fa-eye fs-1" />
        </span>
      </div>
    </div>
  );
};

/**
 * Atoms: LomavisPostCard2
 */
export const LomavisPostCard2: React.FC<LomavisPostCard2Props> = ({ data }) => {
  //   const t = translateComponent('LomavisPostCard2');

  return (
    <div className="w-100 h-100 d-flex flex-column gap-2">
      <div className="bg-white rounded-2 p-2 border flex-fill d-flex flex-column gap-2">
        <div>
          <div className="d-flex align-items-center gap-2 py-2">
            <ReactImageFallback
              src={data?.profile.imageUrl}
              fallbackImage={
                <div className="d-flex w-100 h-100 align-items-center justify-content-center bg-secondary">
                  <i className="fa-regular fa-user fs-1 text-muted"></i>
                </div>
              }
              alt="profile image"
              className="w-50px h-50px rounded-circle object-fit-fill"
            />
            <div className="d-flex flex-column gap-1">
              <span className="fw-bold">{data?.profile?.name}</span>
              <span className="text-muted fw-bold">
                {' '}
                {getDateTimeFormated(data?.publicationDate)}
              </span>
            </div>
          </div>
        </div>

        {/* Image section */}
        <div className="flex-fill d-flex position-relative">
          {(data?.postType === 'multiImage' || data?.postType === 'video') && (
            <span
              style={{ zIndex: 2, borderBottomLeftRadius: '8px' }}
              className="position-absolute top-0 end-0 p-2 bg-white"
            >
              <i
                className={`fa-regular ${
                  data?.postType === 'multiImage'
                    ? 'fa-layer-group'
                    : data?.postType === 'video'
                    ? 'fa-play'
                    : ''
                } `}
              ></i>
            </span>
          )}
          <LomavisImage
            src={data?.media[0]?.url || data?.media[0]?.preview_image}
            fallback={data?.media[0]?.preview_image}
            alt="card image"
          />
        </div>

        <div className="w-100 d-flex justify-content-center align-items-center gap-2 py-3">
          <span className="d-flex align-items-center gap-1">
            <i className="fa-solid fa-thumbs-up text-primary"></i>
            <LomavisShortenedNumber value={data?.analytics?.reactions} />
          </span>
          <span className="d-flex align-items-center gap-1">
            <i className="fa-regular fa-message-dots text-primary"></i>
            <LomavisShortenedNumber value={data?.analytics?.comments} />
          </span>
          <span className="d-flex align-items-center gap-1">
            <i className="fa-regular fa-face-smile text-primary"></i>
            <LomavisShortenedNumber value={data?.analytics?.impressions} />
          </span>
        </div>
      </div>
    </div>
  );
};
